import { Loader2, LogOut, Mail, XCircle } from "lucide-react";
import type React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";

interface ApprovalGateProps {
  children: React.ReactNode;
}

export const ApprovalGate: React.FC<ApprovalGateProps> = ({ children }) => {
  const { user, userProfile, loading, signOut } = useAuth();

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="flex items-center space-x-2 text-white">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // If not authenticated, let the auth flow handle it
  if (!user || !userProfile) {
    return <>{children}</>;
  }

  // Approval gate disabled - all users can access immediately
  // Previously checked for pending approval status, now bypassed
  // Only rejected users are blocked, pending and approved users can access

  if (userProfile.approval_status === "rejected") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-2xl">Account Not Approved</CardTitle>
            <CardDescription>
              Unfortunately, your account registration was not approved for the beta program.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              If you believe this is an error, please contact support for assistance.
            </p>

            {/* User email display */}
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <Mail className="h-4 w-4" />
              <span>{userProfile.email}</span>
            </div>

            {/* Logout button */}
            <Button variant="outline" onClick={signOut} className="w-full">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // All users (pending or approved) can access the application
  return <>{children}</>;
};
